import { useState } from 'react'
import { createPortal } from 'react-dom'
import { createRoot } from 'react-dom/client'
import { Trash2 } from 'lucide-react'
import { AppHeaderIconButton } from '../components/AppHeaderIconButton'
import { CoreButton, CoreDialogSheet } from '../design-system/core'
import { color } from '../tokens'
import tokens from '../generated/design-tokens.css?inline'
import coreStyles from '../design-system/core/core-components.css?inline'
import toolbarStyles from '../components/toolbar-control.css?inline'
import type { SentFeedback } from './chrome'

type Entry = { item: SentFeedback; host: HTMLElement }
export function mountWithdrawalControls(shadow: ShadowRoot, input: {
  onWithdraw: (item: SentFeedback) => Promise<{ withdrawnAt: string }>
  onSuccess: (item: SentFeedback, withdrawnAt: string) => void
}) {
  const style = document.createElement('style')
  style.textContent = tokens.replace(':root', ':host') + coreStyles + toolbarStyles + `
    .feedback-row{position:relative}.feedback-row .feed-head{padding-right:var(--kw-component-button-height-touch)}
    .feedback-actions{position:absolute;right:var(--kw-primitive-space-2);top:var(--kw-primitive-space-1);pointer-events:auto}
    .feedback-row+.feedback-row{margin-top:var(--kw-semantic-space-control-gap)}
  `
  shadow.append(style)
  const host = document.createElement('div'); shadow.append(host)
  const root = createRoot(host)
  function Controls({ entries }: { entries: Entry[] }) {
    const [target, setTarget] = useState<SentFeedback | null>(null)
    const [busy, setBusy] = useState(false)
    const [error, setError] = useState('')
    const [message, setMessage] = useState('')
    const close = () => { if (!busy) { setTarget(null); setError('') } }
    async function confirm() {
      if (!target || busy) return
      setBusy(true); setError('')
      try {
        const result = await input.onWithdraw(target)
        setTarget(null); setMessage('Feedback zurückgenommen.')
        input.onSuccess(target, result.withdrawnAt)
      } catch (err) { setError(err instanceof Error ? err.message : 'Rücknahme nicht gespeichert. Erneut versuchen.') }
      finally { setBusy(false) }
    }
    return <>
      {entries.map(({ item, host }) => createPortal(<AppHeaderIconButton appearance="flat" hitSize={44}
        surfaceStyle={{ color: color.inkSoft }} aria-label="Feedback zurücknehmen" title="Feedback zurücknehmen"
        onClick={() => { setError(''); setMessage(''); setTarget(item) }}><Trash2 size={15} strokeWidth={1.5} aria-hidden="true" /></AppHeaderIconButton>, host, item.todoKey))}
      <span role="status" aria-live="polite" style={{ position: 'fixed', width: 1, height: 1, overflow: 'hidden', clipPath: 'inset(50%)' }}>{message}</span>
      <CoreDialogSheet open={!!target} title="Feedback zurücknehmen?" initialFocus="dialog" onClose={close}
        footer={<><CoreButton disabled={busy} onClick={close}>Abbrechen</CoreButton><CoreButton variant="primary" busy={busy} busyLabel="Wird zurückgenommen …" onClick={() => void confirm()}>Zurücknehmen</CoreButton></>}>
        <p>„{target?.preview}“</p>
        <p>Dein Feedback gilt danach nicht mehr als offener Änderungswunsch. Der Eintrag bleibt im Verlauf erhalten. Bereits begonnene Arbeiten werden dadurch nicht automatisch gestoppt.</p>
        {error && <p role="alert">{error}</p>}
      </CoreDialogSheet>
    </>
  }
  return { render(entries: Entry[]) { root.render(<Controls entries={entries} />) }, destroy() { root.unmount(); host.remove(); style.remove() } }
}
